import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSort, setCounterOffer, setFeedback, getHighlights, TAGS_SORT_KEY } from '../../redux/slices/selectionSlice';

const stat = (ind, grp) => `${ind ?? '—'}/${grp ?? '—'}`;

// Must stay in the same order as the <td>s in TableRow. The paired count columns
// sort on the individual value (the first of the two numbers shown).
const SORTABLE_COLS = [
  { key: 'LD ID',       label: 'LD ID' },
  { key: 'Supplier',    label: 'Supplier' },
  { key: 'Shape',       label: 'Shape' },
  { key: 'Carats',      label: 'Cts' },
  { key: 'Color',       label: 'Color' },
  { key: 'Clarity',     label: 'Clarity' },
  { key: 'Rap %',       label: 'Rap %' },
  { key: '$/Ct',        label: 'PPC' },
  { key: 'Total Price', label: 'Total' },
  { key: 'Demand Name', label: 'Demand' },
  { key: 'Location',    label: 'Location' },
  { key: 'Status',      label: 'Status' },
  { key: 'inv',         label: 'CurrentInv', title: 'Current Inventory (individual / group)' },
  { key: 'sales_90d',   label: '90D Sales',  title: '90 Day Sales (individual / group)' },
  { key: 'sales_ytd',   label: 'YTD Sales',  title: 'Year-to-date Sales (individual / group)' },
  { key: 'sales_1y',    label: '1Y Sales',   title: '1 Year Sales (individual / group)' },
  { key: 'need_2x',     label: 'Need (2X)',  title: 'Need at 2x (individual / group)' },
  { key: TAGS_SORT_KEY, label: 'Tags',       title: 'Sorts by tag name' },
];

function calcOffer(type, value, diamond) {
  const listPrice = parseFloat(diamond['Rap $/Ct'] || 0) ||
    (() => {
      const ppc    = parseFloat(diamond['$/Ct'] || 0);
      const rapPct = parseFloat(diamond['Rap %'] || 0);
      return rapPct !== -100 ? ppc / (1 + rapPct / 100) : 0;
    })();
  const carats = parseFloat(diamond.Carats || 0);

  let offerRap, offerPPC, offerTotal;
  if (type === 'rap') {
    offerRap   = parseFloat(value);
    offerPPC   = listPrice * (1 + offerRap / 100);
    offerTotal = offerPPC * carats;
  } else if (type === 'ppc') {
    offerPPC   = parseFloat(value);
    offerTotal = offerPPC * carats;
    offerRap   = listPrice > 0 ? ((offerPPC / listPrice) - 1) * 100 : 0;
  } else {
    offerTotal = parseFloat(value);
    offerPPC   = carats > 0 ? offerTotal / carats : 0;
    offerRap   = listPrice > 0 ? ((offerPPC / listPrice) - 1) * 100 : 0;
  }
  return {
    offerRap: offerRap.toFixed(2), offerPPC: offerPPC.toFixed(2), offerTotal: offerTotal.toFixed(2),
  };
}

const SortIcon = ({ colKey, sort }) => {
  if (sort.column !== colKey) return <span style={{ color: '#ccc', marginLeft: 3 }}>↕</span>;
  return <span style={{ color: '#007bff', marginLeft: 3 }}>{sort.order === 'asc' ? '↑' : '↓'}</span>;
};

const TableRow = React.memo(({ diamond }) => {
  const dispatch    = useDispatch();
  const ldId        = String(diamond['LD ID']);
  const counterOffer = useSelector(s => s.selection.counterOffers[ldId]) || {};
  const feedbackVal  = useSelector(s => s.selection.feedback[ldId])      || '';

  const handleOffer = useCallback((e, type) => {
    if (!e.target.value) return;
    const computed = calcOffer(type, e.target.value, diamond);
    dispatch(setCounterOffer({ ldId, ...computed }));
  }, [diamond, dispatch, ldId]);

  const handleFeedback = useCallback(e => {
    dispatch(setFeedback({ ldId, value: e.target.value }));
  }, [dispatch, ldId]);

  const highlights = getHighlights(diamond);

  const statusColor = {
    Shortlisted: '#28a745',
    Rejected:    '#dc3545',
  }[diamond.Status] || '#6c757d';

  return (
    <tr>
      <td>{diamond['LD ID']}</td>
      <td>{diamond.Supplier}</td>
      <td>{diamond.Shape}</td>
      <td>{diamond.Carats}</td>
      <td>{diamond.Color}</td>
      <td>{diamond.Clarity}</td>
      <td>{diamond['Rap %']}%</td>
      <td>${parseFloat(diamond['$/Ct'] || 0).toLocaleString()}</td>
      <td>${parseFloat(diamond['Total Price'] || 0).toLocaleString()}</td>
      <td style={{ maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {diamond['Demand Name']}
      </td>
      <td>{diamond.Location}</td>
      <td>
        {diamond.Status && (
          <span style={{ color: statusColor, fontWeight: 600, fontSize: 11 }}>
            {diamond.Status}
          </span>
        )}
      </td>
      <td style={{ textAlign: 'right' }}>{stat(diamond.inv, diamond.grp_inv)}</td>
      <td style={{ textAlign: 'right' }}>{stat(diamond.sales_90d, diamond.grp_sales_90d)}</td>
      <td style={{ textAlign: 'right' }}>{stat(diamond.sales_ytd, diamond.grp_sales_ytd)}</td>
      <td style={{ textAlign: 'right' }}>{stat(diamond.sales_1y, diamond.grp_sales_1y)}</td>
      <td style={{ textAlign: 'right' }}>{stat(diamond.need_2x, diamond.grp_need_2x)}</td>
      {/* Tags */}
      <td>
        {highlights.map((h, i) => (
          <span key={i} className={`sel-tag ${h.type}`} style={{ marginRight: 2 }}>{h.text}</span>
        ))}
      </td>
      {/* Offer inputs */}
      <td className="sel-offer-cell">
        <input
          type="number" placeholder="Rap%"
          defaultValue={counterOffer.offerRap || ''}
          key={`rap-${ldId}-${counterOffer.offerRap}`}
          onBlur={e => handleOffer(e, 'rap')}
        />
      </td>
      <td className="sel-offer-cell">
        <input
          type="number" placeholder="PPC"
          defaultValue={counterOffer.offerPPC || ''}
          key={`ppc-${ldId}-${counterOffer.offerPPC}`}
          onBlur={e => handleOffer(e, 'ppc')}
        />
      </td>
      <td className="sel-offer-cell">
        <input
          type="number" placeholder="Total"
          defaultValue={counterOffer.offerTotal || ''}
          key={`total-${ldId}-${counterOffer.offerTotal}`}
          onBlur={e => handleOffer(e, 'total')}
        />
      </td>
      <td className="sel-feedback-cell">
        <textarea value={feedbackVal} onChange={handleFeedback} placeholder="Feedback…" />
      </td>
    </tr>
  );
});

const DiamondTable = ({ diamonds }) => {
  const dispatch = useDispatch();
  const sort = useSelector(s => s.selection.sort);

  return (
    <div className="sel-table-wrap">
      <table className="sel-table">
        <thead>
          <tr>
            {SORTABLE_COLS.map(({ key, label, title }) => (
              <th key={key} className="sortable" title={title} onClick={() => dispatch(toggleSort(key))}>
                {label}<SortIcon colKey={key} sort={sort} />
              </th>
            ))}
            <th>Offer Rap</th>
            <th>Offer PPC</th>
            <th>Offer Total</th>
            <th>Feedback</th>
          </tr>
        </thead>
        <tbody>
          {diamonds.map(d => (
            <TableRow key={d['LD ID']} diamond={d} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DiamondTable;
